import { RefObject, useEffect } from "react"

const DEFAULT_RESIZE_MESSAGE_TYPE = "resize"

type UseEmbedResizeMessagingOptions = {
  containerRef: RefObject<HTMLElement | null>
  source: string
  messageType?: string
}

const useEmbedResizeMessaging = ({
  containerRef,
  source,
  messageType = DEFAULT_RESIZE_MESSAGE_TYPE
}: UseEmbedResizeMessagingOptions) => {
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      window.parent === window ||
      !containerRef.current
    ) {
      return
    }

    let rafId: number | null = null

    const postEmbedSize = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const documentHeight = Math.max(
        document.documentElement?.scrollHeight || 0,
        document.body?.scrollHeight || 0
      )
      const nextHeight = Math.max(Math.ceil(rect.height), documentHeight)
      window.parent.postMessage(
        {
          source,
          type: messageType,
          height: nextHeight,
          width: Math.ceil(rect.width)
        },
        "*"
      )
    }

    const schedulePostEmbedSize = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        postEmbedSize()
      })
    }

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedulePostEmbedSize)
        : null

    observer?.observe(containerRef.current)
    if (document.body) {
      observer?.observe(document.body)
    }
    window.addEventListener("load", schedulePostEmbedSize)
    window.addEventListener("resize", schedulePostEmbedSize)
    schedulePostEmbedSize()

    return () => {
      observer?.disconnect()
      window.removeEventListener("load", schedulePostEmbedSize)
      window.removeEventListener("resize", schedulePostEmbedSize)
      if (rafId !== null) window.cancelAnimationFrame(rafId)
    }
  }, [containerRef, messageType, source])
}

export default useEmbedResizeMessaging
